import { Card, ConfigProvider, Form, TableProps } from 'antd'
import vi_VN from 'antd/es/locale/vi_VN'
import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiGetOutletStats } from '../../api'
import { DETAIL, OUTLET } from '../../assets/constants/route_path'
import FormSearch from '../../components/form'
import CompanySelect from '../../components/outlet/CompanySelect'
import DateRangePresets from '../../components/outlet/DateRangePresets'
import OutletSelect from '../../components/outlet/OutletSelect'
import StatusTag from '../../components/outlet/StatusTag'
import CustomTable from '../../components/table'
import { useLoading } from '../../context/LoadingContext'
import { IOutletStatsResponse, ITopOutlet } from '../../models/outlet.modal'
import { toastError } from '../../utils/toast'

const formatNumber = (v?: number | null) => (v == null ? '—' : v.toLocaleString('vi-VN'))
const formatCurrency = (v?: number | null) => (v == null ? '—' : `${v.toLocaleString('vi-VN')}₫`)
const formatPercent = (rate?: number | null) => (rate == null ? '—' : `${(rate * 100).toFixed(1)}%`)

type SearchState = {
    fromDate?: string
    toDate?: string
    cpCodes?: string[]
    outletIds?: string[]
}

export default function OutletStats() {
    const [stats, setStats] = useState<IOutletStatsResponse | null>(null)
    const [search, setSearch] = useState<SearchState>({})
    const { setIsLoading } = useLoading()
    const [formSearch] = Form.useForm()

    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true)
            const resp: any = await apiGetOutletStats({
                fromDate: search.fromDate,
                toDate: search.toDate,
                cpCodes: search.cpCodes,
                outletIds: search.outletIds,
            })
            setStats(resp?.data ?? null)
        } catch (error) {
            console.log(error)
            toastError('Không tải được thống kê CRM đại lý')
            setStats(null)
        } finally {
            setIsLoading(false)
        }
    }, [setIsLoading, search])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    const handleSearch = (values: any) => {
        const range = values.range as [dayjs.Dayjs, dayjs.Dayjs] | undefined
        setSearch({
            fromDate: range?.[0]?.format('YYYY-MM-DD'),
            toDate: range?.[1]?.format('YYYY-MM-DD'),
            cpCodes: values.cpCodes,
            outletIds: values.outletIds,
        })
    }

    const handleClearSearch = () => {
        formSearch.resetFields()
        setSearch({})
    }

    const topColumns: TableProps<ITopOutlet>['columns'] = [
        {
            key: 'outletName',
            title: 'Đại lý',
            render: (_, r) => <Link to={OUTLET + DETAIL + `?id=${r.outletId}`}>{r.outletName || '(không tên)'}</Link>,
        },
        {
            key: 'province',
            title: 'Khu vực',
            dataIndex: 'province',
            render: v => v || '—',
        },
        {
            key: 'simAllocated',
            title: 'SIM giao',
            align: 'right',
            render: (_, r) => formatNumber(r.simAllocated),
        },
        {
            key: 'simActivated',
            title: 'SIM đấu mới',
            align: 'right',
            render: (_, r) => formatNumber(r.simActivated),
        },
        {
            key: 'renewalCount',
            title: 'Lượt gia hạn',
            align: 'right',
            render: (_, r) => formatNumber(r.renewalCount),
        },
        {
            key: 'revenue',
            title: 'Doanh thu',
            align: 'right',
            render: (_, r) => formatCurrency(r.revenue),
        },
    ]

    const statusBreakdown = stats?.outletsByStatus ?? {}

    return (
        <ConfigProvider locale={vi_VN}>
            <FormSearch form={formSearch} handleSearch={handleSearch} handleClearSearch={handleClearSearch}>
                <Form.Item name='range' label='Khoảng ngày' style={{ minWidth: 300 }}>
                    <DateRangePresets />
                </Form.Item>
                <Form.Item name='cpCodes' label='Công ty' style={{ minWidth: 240 }}>
                    <CompanySelect multiple onlyWithOutlets placeholder='Chọn công ty' />
                </Form.Item>
                <Form.Item name='outletIds' label='Đại lý' style={{ minWidth: 240 }}>
                    <OutletSelect multiple placeholder='Chọn đại lý' />
                </Form.Item>
            </FormSearch>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 20 }}>
                <Card title='Tổng đại lý'>
                    <div style={{ fontSize: 28, fontWeight: 700, color: '#7940ee' }}>
                        {formatNumber(stats?.totalOutlets)}
                    </div>
                    <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {Object.entries(statusBreakdown).map(([status, count]) => (
                            <span key={status} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <StatusTag status={status} />
                                <b>{formatNumber(count)}</b>
                            </span>
                        ))}
                        {Object.keys(statusBreakdown).length === 0 && <span style={{ color: '#999' }}>—</span>}
                    </div>
                </Card>
                <Card title='Tổng SIM giao'>
                    <div style={{ fontSize: 28, fontWeight: 700, color: '#1677ff' }}>
                        {formatNumber(stats?.totalSimAllocated)}
                    </div>
                </Card>
                <Card title='SIM đấu mới'>
                    <div style={{ fontSize: 28, fontWeight: 700, color: '#52c41a' }}>
                        {formatNumber(stats?.totalSimActivated)}
                    </div>
                    <div style={{ marginTop: 8, color: '#666' }}>
                        Tỷ lệ đấu: <b>{formatPercent(stats?.activationRate)}</b>
                    </div>
                </Card>
                <Card title='Lượt gia hạn'>
                    <div style={{ fontSize: 28, fontWeight: 700, color: '#faad14' }}>
                        {formatNumber(stats?.totalRenewalCount)}
                    </div>
                </Card>
                <Card title='Tổng doanh thu'>
                    <div style={{ fontSize: 28, fontWeight: 700, color: '#ec4969' }}>
                        {formatCurrency(stats?.totalRevenue)}
                    </div>
                    {(stats?.totalRevenueActivation != null || stats?.totalRevenueRenewal != null) && (
                        <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
                            <div>Đấu mới: <b>{formatCurrency(stats?.totalRevenueActivation)}</b></div>
                            <div>Gia hạn: <b>{formatCurrency(stats?.totalRevenueRenewal)}</b></div>
                        </div>
                    )}
                </Card>
            </div>

            <Card title='Top 10 đại lý theo doanh thu' style={{ marginBottom: 20 }}>
                <CustomTable
                    namePage='đại lý'
                    rowKey='outletId'
                    columns={topColumns}
                    dataSource={stats?.topOutlets ?? []}
                    total={stats?.topOutlets?.length ?? 0}
                    pageSize={10}
                    setPageSize={() => { }}
                    currentPage={1}
                    setCurrentPage={() => { }}
                    isHiddenPagination
                />
            </Card>
        </ConfigProvider>
    )
}
